// regionConfig.js — multi-region base-URL registry for the editor.
//
// Phase 1a: static defaults = prod US.
// Phase 2c: signing/view sessions set the active region from the JWT claim
// (or hashIds region_code / bulk resolve), then all API/link calls follow it.

const env = (typeof process !== "undefined" && process.env) || {};

export const REGIONS = {
  us: {
    regionCode: "us",
    displayName: "United States",
    api: env.REACT_APP_US_API_BASE_URL || "https://server.requiresign.com/",
    portal: env.REACT_APP_US_PORTAL_BASE_URL || "https://portal.requiresign.com/",
    editor: env.REACT_APP_US_EDITOR_BASE_URL || "https://editor.requiresign.com",
    link: env.REACT_APP_US_LINK_BASE_URL || "https://link.requiresign.com",
  },
  sg: {
    regionCode: "sg",
    displayName: "Singapore",
    api: env.REACT_APP_SG_API_BASE_URL || "https://sg-server.requiresign.com/",
    portal: env.REACT_APP_SG_PORTAL_BASE_URL || "https://portal.requiresign.com/",
    editor: env.REACT_APP_SG_EDITOR_BASE_URL || "https://editor.requiresign.com",
    link: env.REACT_APP_SG_LINK_BASE_URL || "https://sg-link.requiresign.com",
  },
  in: {
    regionCode: "in",
    displayName: "India",
    api: env.REACT_APP_IN_API_BASE_URL || "https://in-server.requiresign.com/",
    portal: env.REACT_APP_IN_PORTAL_BASE_URL || "https://portal.requiresign.com/",
    editor: env.REACT_APP_IN_EDITOR_BASE_URL || "https://editor.requiresign.com",
    link: env.REACT_APP_IN_LINK_BASE_URL || "https://in-link.requiresign.com",
  },
  ng: {
    regionCode: "ng",
    displayName: "Nigeria",
    api: env.REACT_APP_NG_API_BASE_URL || "https://ng-server.mtechub.org/",
    portal: env.REACT_APP_NG_PORTAL_BASE_URL || "https://portal.requiresign.com/",
    editor: env.REACT_APP_NG_EDITOR_BASE_URL || "https://editor.requiresign.com",
    link: env.REACT_APP_NG_LINK_BASE_URL || "https://ng-link.mtechub.org",
  },
};

export const DEFAULT_REGION = (env.REACT_APP_DEFAULT_REGION || "us").toLowerCase();
const SESSION_KEY = "rs_active_region";

export const getRegion = (regionCode) =>
  REGIONS[String(regionCode || DEFAULT_REGION).toLowerCase()] || REGIONS.us;

export const isValidRegion = (regionCode) =>
  Boolean(regionCode && REGIONS[String(regionCode).toLowerCase()]);

export function getActiveRegionCode() {
  try {
    const fromSession =
      typeof sessionStorage !== "undefined"
        ? sessionStorage.getItem(SESSION_KEY)
        : null;
    if (isValidRegion(fromSession)) return fromSession.toLowerCase();
  } catch (_e) {
    /* ignore */
  }
  return DEFAULT_REGION;
}

export function getActiveRegion() {
  return getRegion(getActiveRegionCode());
}

export function setActiveRegion(regionCode) {
  const code = isValidRegion(regionCode)
    ? String(regionCode).toLowerCase()
    : DEFAULT_REGION;
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(SESSION_KEY, code);
    }
  } catch (_e) {
    /* ignore */
  }
  return getRegion(code);
}

export function getApiBaseUrl() {
  const api = getActiveRegion().api || REGIONS.us.api;
  return api.endsWith("/") ? api : `${api}/`;
}

export function getLinkBaseUrl() {
  return (getActiveRegion().link || REGIONS.us.link).replace(/\/+$/, "");
}

/**
 * Decode a JWT payload client-side (no verify — region claim is routing only).
 * Missing/malformed → null.
 */
export function decodeJwtPayload(token) {
  try {
    if (!token || typeof token !== "string") return null;
    // Token may be AES-encrypted path segment OR raw JWT. Try raw JWT first.
    const parts = token.split(".");
    if (parts.length >= 2) {
      let payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
      payload += "=".repeat((4 - (payload.length % 4)) % 4);
      return JSON.parse(atob(payload));
    }
  } catch (_e) {
    /* ignore */
  }
  return null;
}

/**
 * Read region from a signing-link JWT (path segment). Defaults to "us".
 */
export function regionFromJwtToken(token) {
  const payload = decodeJwtPayload(token);
  const code = payload?.region;
  return isValidRegion(code) ? String(code).toLowerCase() : DEFAULT_REGION;
}

/**
 * Read region_code from a decrypted hashIds object. Defaults to "us".
 */
export function regionFromHashPayload(obj) {
  const code = obj?.region_code;
  return isValidRegion(code) ? String(code).toLowerCase() : DEFAULT_REGION;
}
